const API_BASE = process.env.REACT_APP_API_URL || "http://localhost:3001/api";

function getHeaders(token) {
  const headers = { "Content-Type": "application/json" };
  if (token) headers.Authorization = `Bearer ${token}`;
  return headers;
}

async function request(path, options = {}) {
  const controller = options.signal ? null : new AbortController();
  const timeoutId = controller ? setTimeout(() => controller.abort(), 15000) : null;
  try {
    const res = await fetch(`${API_BASE}${path}`, {
      ...options,
      signal: options.signal || controller.signal,
    });
    const data = await res.json();
    if (!res.ok) {
      const err = new Error(data.error || "Request failed");
      err.status = res.status;
      err.data = data;
      throw err;
    }
    return data;
  } finally {
    if (timeoutId) clearTimeout(timeoutId);
  }
}

// Auth
export const auth = {
  register: (email, password, username, firstName, lastName) =>
    request("/auth/register", {
      method: "POST",
      headers: getHeaders(),
      body: JSON.stringify({ email, password, username, firstName, lastName }),
    }),

  login: (email, password) =>
    request("/auth/login", {
      method: "POST",
      headers: getHeaders(),
      body: JSON.stringify({ email, password }),
    }),

  me: (token) =>
    request("/auth/me", { headers: getHeaders(token) }),

  verify: (token, code) =>
    request("/auth/verify", {
      method: "POST",
      headers: getHeaders(token),
      body: JSON.stringify({ code }),
    }),

  resendCode: (token) =>
    request("/auth/resend-code", {
      method: "POST",
      headers: getHeaders(token),
    }),

  forgotPassword: (email) =>
    request("/auth/forgot-password", {
      method: "POST",
      headers: getHeaders(),
      body: JSON.stringify({ email }),
    }),

  resetPassword: (email, code, newPassword) =>
    request("/auth/reset-password", {
      method: "POST",
      headers: getHeaders(),
      body: JSON.stringify({ email, code, newPassword }),
    }),
};

// Posts
export const posts = {
  getAll: (params = {}) => {
    const query = new URLSearchParams(params).toString();
    return request(`/posts${query ? `?${query}` : ""}`);
  },

  getById: (id, token) =>
    request(`/posts/${id}`, {
      headers: token ? getHeaders(token) : {},
    }),

  create: (token, postData) =>
    request("/posts", {
      method: "POST",
      headers: getHeaders(token),
      body: JSON.stringify(postData),
    }),

  update: (token, id, updates) =>
    request(`/posts/${id}`, {
      method: "PUT",
      headers: getHeaders(token),
      body: JSON.stringify(updates),
    }),

  delete: (token, id) =>
    request(`/posts/${id}`, {
      method: "DELETE",
      headers: getHeaders(token),
    }),
};

// Users
export const users = {
  getById: (id, token) => request(`/users/${id}`, { headers: getHeaders(token) }),

  update: (token, id, updates) =>
    request(`/users/${id}`, {
      method: "PUT",
      headers: getHeaders(token),
      body: JSON.stringify(updates),
    }),

  block: (token, id) =>
    request(`/users/${id}/block`, {
      method: "POST",
      headers: getHeaders(token),
    }),

  unblock: (token, id) =>
    request(`/users/${id}/block`, {
      method: "DELETE",
      headers: getHeaders(token),
    }),
};

// Content reports (UGC safety)
export const reports = {
  create: (token, contentType, contentId, reason, details) =>
    request("/reports", {
      method: "POST",
      headers: getHeaders(token),
      body: JSON.stringify({ contentType, contentId, reason, details }),
    }),
};

// Comments
export const comments = {
  getByPost: (postId) => request(`/posts/${postId}/comments`),

  create: (token, postId, body) =>
    request(`/posts/${postId}/comments`, {
      method: "POST",
      headers: getHeaders(token),
      body: JSON.stringify({ body }),
    }),

  delete: (token, postId, commentId) =>
    request(`/posts/${postId}/comments/${commentId}`, {
      method: "DELETE",
      headers: getHeaders(token),
    }),
};

// Upload
export const upload = {
  avatar: async (token, file) => {
    const formData = new FormData();
    formData.append("image", file);
    const res = await fetch(`${API_BASE}/upload/avatar`, {
      method: "POST",
      headers: { Authorization: `Bearer ${token}` },
      body: formData,
    });
    const data = await res.json();
    if (!res.ok) {
      const err = new Error(data.error || "Avatar upload failed");
      err.status = res.status;
      throw err;
    }
    return data;
  },

  image: async (token, file) => {
    const formData = new FormData();
    formData.append("image", file);
    const res = await fetch(`${API_BASE}/upload`, {
      method: "POST",
      headers: { Authorization: `Bearer ${token}` },
      body: formData,
    });
    const data = await res.json();
    if (!res.ok) {
      const err = new Error(data.error || "Upload failed");
      err.status = res.status;
      throw err;
    }
    return data;
  },
};

// Notifications
export const notifications = {
  getAll: (token) =>
    request("/notifications", { headers: getHeaders(token) }),

  getUnreadCount: (token) =>
    request("/notifications/unread-count", { headers: getHeaders(token) }),

  markRead: (token) =>
    request("/notifications/mark-read", {
      method: "POST",
      headers: getHeaders(token),
    }),
};

// Devices (push notification tokens)
export const devices = {
  register: (token, deviceToken, platform) =>
    request("/devices", {
      method: "POST",
      headers: getHeaders(token),
      body: JSON.stringify({ token: deviceToken, platform }),
    }),

  remove: (token, deviceToken) =>
    request("/devices", {
      method: "DELETE",
      headers: getHeaders(token),
      body: JSON.stringify({ token: deviceToken }),
    }),
};

// Cities
export const cities = {
  getAll: () => request("/cities"),
  getByName: (city, category) => {
    const params = {};
    if (category) params.category = category;
    const query = new URLSearchParams(params).toString();
    return request(`/cities/${encodeURIComponent(city)}${query ? `?${query}` : ""}`);
  },
};

// Map
export const map = {
  getPins: (bounds, category, token, signal) => {
    const params = {};
    if (bounds) {
      params.north = bounds.north;
      params.south = bounds.south;
      params.east = bounds.east;
      params.west = bounds.west;
    }
    if (category) params.category = category;
    const query = new URLSearchParams(params).toString();
    return request(`/map/pins${query ? `?${query}` : ""}`, {
      headers: token ? getHeaders(token) : {},
      signal,
    });
  },
};
